import { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const METRIC_COLOR = {
  above: '#ef4444',
  borderline: '#d97706',
  at_target: '#16a34a',
  normal: '#111111',
  unknown: '#ef4444',
};

const SmartPrepCard = ({ prep, loading, error, onRetry, onRefresh, visitContext }) => {
  const [expanded, setExpanded] = useState(true);
  const [showMore, setShowMore] = useState(false);

  if (loading) {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: '10px 16px' }}
        className="animate-pulse" data-testid="smart-prep-loading">
        <div className="flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 text-violet-500 animate-spin" />
          <span style={{ fontSize: 13, fontWeight: 500 }}>Loading Smart Prep...</span>
        </div>
        <div className="mt-3 space-y-2">
          <div className="h-3 bg-gray-100 rounded w-full" />
          <div className="h-3 bg-gray-100 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: '10px 16px' }}
        data-testid="smart-prep-error">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div style={{ width: 22, height: 22, borderRadius: 6, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ccc' }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#888' }}>Smart Prep unavailable</span>
          </div>
          <button onClick={onRetry} style={{ fontSize: 11, color: '#6b5ce7', cursor: 'pointer', background: 'none', border: 'none', fontWeight: 500 }}
            data-testid="smart-prep-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (!prep) return null;

  // Truncate summary to 2 sentences
  const fullSummary = prep.summary || '';
  const sentences = fullSummary.match(/[^.!?]+[.!?]+/g) || [fullSummary];
  const shortSummary = sentences.slice(0, 2).join(' ').trim();
  const hasMoreSummary = sentences.length > 2;

  // Limit action items to 3
  const allItems = prep.openItems || [];
  const visibleItems = allItems.slice(0, 3);
  const hiddenCount = Math.max(0, allItems.length - 3);

  return (
    <div style={{ background: '#ffffff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }}
      data-testid="smart-prep-card">
      {/* Header row — clickable to collapse/expand */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
        style={{ padding: '10px 16px', borderBottom: expanded ? '0.5px solid #f0f0f0' : 'none', cursor: 'pointer', background: 'transparent' }}
        data-testid="smart-prep-toggle"
      >
        <div className="flex items-center gap-2">
          <div style={{
            width: 22, height: 22, borderRadius: 6, background: '#f0edff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6b5ce7' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>Smart Prep</span>
          <span style={{
            fontSize: 10, background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '1px 7px',
          }}>AI Pre-Visit</span>
          {prep._source === 'cache' && (
            <span style={{ fontSize: 10, background: '#f5f5f5', color: '#888888', borderRadius: 20, padding: '1px 7px' }}
              data-testid="smart-prep-cache-badge">Cached</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {visitContext && (
            <span style={{ fontSize: 11, color: '#aaaaaa' }}>{visitContext}</span>
          )}
          {expanded
            ? <ChevronUp style={{ width: 11, height: 11, color: '#cccccc' }} />
            : <ChevronDown style={{ width: 11, height: 11, color: '#cccccc' }} />
          }
        </div>
      </button>

      {/* Body — collapsible */}
      {expanded && (
        <div style={{ padding: '12px 16px' }}>
          {/* Summary text — 2 sentences max */}
          <p style={{ fontSize: 13, color: '#444444', lineHeight: 1.6, marginBottom: 12 }}
            data-testid="smart-prep-summary">
            {showMore ? fullSummary : shortSummary}
            {hasMoreSummary && !showMore && (
              <button onClick={(e) => { e.stopPropagation(); setShowMore(true); }}
                style={{ fontSize: 12, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4, fontWeight: 500 }}>
                Show more
              </button>
            )}
          </p>

          {/* Metrics grid — 4 tiles */}
          {prep.keyMetrics && prep.keyMetrics.length > 0 && (
            <div className="grid grid-cols-4 gap-2" style={{ marginBottom: 12 }} data-testid="smart-prep-metrics">
              {prep.keyMetrics.slice(0, 4).map((m, i) => {
                const color = m.status === 'above' ? METRIC_COLOR.above
                  : m.status === 'borderline' ? METRIC_COLOR.borderline
                  : m.status === 'at_target' ? METRIC_COLOR.at_target
                  : m.value === 'Unknown' ? METRIC_COLOR.unknown
                  : METRIC_COLOR.normal;
                return (
                  <div key={i} style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }} data-testid={`metric-tile-${i}`}>
                    <p style={{ fontSize: 10, color: '#aaaaaa', marginBottom: 2 }}>{m.label}</p>
                    <p style={{ fontSize: 14, fontWeight: 500, color }}>{m.value}</p>
                    {m.target && <p style={{ fontSize: 10, color: '#bbbbbb' }}>Target {m.target}</p>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Action items — max 3 */}
          {visibleItems.length > 0 && (
            <div>
              <p style={{
                fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
              }}>Action items</p>
              {visibleItems.map((item, i) => (
                <div key={i} className="flex items-start gap-2" style={{ padding: '3px 0' }}>
                  <div style={{
                    width: 4, height: 4, borderRadius: '50%', background: '#6b5ce7',
                    marginTop: 6, flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 12, color: '#555555' }}>{item}</span>
                </div>
              ))}
              {hiddenCount > 0 && (
                <p style={{ fontSize: 11, color: '#6b5ce7', marginTop: 4, cursor: 'pointer' }}>
                  &middot; &middot; &middot; {hiddenCount} more
                </p>
              )}
            </div>
          )}

          {/* Regenerate link */}
          <div className="flex justify-end mt-2">
            <button onClick={onRefresh}
              style={{ fontSize: 10, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
              data-testid="smart-prep-regenerate">
              Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartPrepCard;
